import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconArrowLeft, IconBell, IconChevronDown, IconChevronLeft, IconListDetails, IconSearch, IconSend, IconBox, IconCalendarEvent, IconClock, IconDots, IconRoute, IconShip, IconTimeline } from '@tabler/icons-react';
import { DataError, useRecord } from '@hana/data';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import { Button, Chip, Notes, StateView, useToast, type ScreenState } from '@hana/ui';
import { JOB_BOARD_NOW } from './fixtures';
import { cutoffLabel, dayLabel } from './format';
import { GROUP_DOT } from './JobCards';
import { useLayout } from './useLayout';
import { OverviewBoard } from './OverviewBoard';
import { gateInCount, isValidContainerNo, transitDays, type JobDetail } from './detail';
import { cutoffHoursLeft, jobGroup } from './jobs';
import './jobDetailI18n';
import './jobListI18n';
import s from './jobDetail.module.css';

// Mint-JobDetail (desktop 1536): đầu trang + dải KPI + tab Tổng quan, Hàng & container. Chỉ đọc; nút ghi báo toast.
// Freight & Charges / Chi phí (tiền) không dựng ở đây — gói sau quyền `money`.

const NOW = JOB_BOARD_NOW;

type TabKey = 'ov' | 'cargo' | 'svc' | 'fc' | 'cost' | 'doc' | 'inv' | 'task' | 'note';
// n = số đếm sau nhãn tab theo board (Mint-JobDetail `TABS`: 0 thì không hiện).
// Chồng avatar đầu trang (HanaAvatar): có ảnh → hình người, chưa có → chữ cái tắt. Chuông: thông báo 3 + việc mở 5 (board `hdrPanels`).
const HAS_PHOTO: Record<string, boolean> = { DAVID: true, MAITTN: true, PHUONGNKT: true, LIENNTK: true, ThuyNTT: true };
const NOTI_N = 8;
const TABS: { key: TabKey; label: string; n: number }[] = [
  { key: 'ov', label: 'jobDetail.tab.ov', n: 0 },
  { key: 'cargo', label: 'jobDetail.tab.cargo', n: 1 },
  { key: 'svc', label: 'jobDetail.tab.svc', n: 1 },
  { key: 'fc', label: 'jobDetail.tab.fc', n: 0 },
  { key: 'cost', label: 'jobDetail.tab.cost', n: 0 },
  { key: 'doc', label: 'jobDetail.tab.doc', n: 3 },
  { key: 'inv', label: 'jobDetail.tab.inv', n: 1 },
  { key: 'task', label: 'jobDetail.tab.task', n: 5 },
  { key: 'note', label: 'jobDetail.tab.note', n: 0 },
];

// Mint-JobDetail-iPad `tabs`: không có Dịch vụ; nhãn rút gọn.
const IPAD_LABEL: Partial<Record<TabKey, string>> = { cargo: 'jobDetail.tab.cargoPad', note: 'jobDetail.tab.notePad' };

const PH_TABS: { key: TabKey; label: string; n: number }[] = [
  { key: 'ov', label: 'jobDetail.ph.ov', n: 5 },
  { key: 'doc', label: 'jobDetail.ph.doc', n: 3 },
  { key: 'inv', label: 'jobDetail.ph.mail', n: 3 },
  { key: 'fc', label: 'jobDetail.ph.fee', n: 0 },
  { key: 'note', label: 'jobDetail.ph.note', n: 0 },
];
function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const fmt = (iso: string | null) => {
  const l = cutoffLabel(iso);
  return l ?? '—';
};

function Card({ title, icon, children, open = true }: { title: string; icon: React.ReactNode; children: React.ReactNode; open?: boolean }) {
  // Điện thoại: thẻ gập (accordion) theo JD-OV2; iPad / máy bàn luôn mở.
  const phone = useLayout() === 'phone';
  const [shown, setShown] = useState(open);
  const body = !phone || shown;
  return (
    <section className={s.card} aria-label={title}>
      <h2 className={s.cardH}>
        {phone ? (
          <button type="button" className={s.cardBtn} aria-expanded={shown} onClick={() => setShown((v) => !v)}>
            {icon}
            <span>{title}</span>
            <IconChevronDown size={18} aria-hidden="true" className={shown ? s.chevOpen : s.chev} />
          </button>
        ) : (
          <>
            {icon}
            {title}
          </>
        )}
      </h2>
      {body && children}
    </section>
  );
}

function Kv({ items }: { items: { label: string; value: string }[] }) {
  return (
    <dl className={s.kv}>
      {items.map((i) => (
        <div key={i.label}>
          <dt>{i.label}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Kpis({ d }: { d: JobDetail }) {
  const t = useT();
  const left = cutoffHoursLeft({ cyCutoff: d.closeCY }, NOW);
  const gate = gateInCount(d);
  const hot = left !== null && left >= 0 && left < 48;
  return (
    <div className={s.kpis} role="list" aria-label={t('jobDetail.kpiAria')}>
      <div role="listitem" className={`${s.kpi} ${hot ? s.kpiHot : ''}`} title={`Closing CY ${fmt(d.closeCY)}`}>
        <IconClock size={16} aria-hidden="true" />
        <span>Cut-off CY</span>
        <b>{left === null ? '—' : left < 0 ? t('jobDetail.cutPast') : left < 24 ? t('jobDetail.cutHours', { n: Math.round(left) }) : t('jobDetail.cutDays', { n: Math.round(left / 24) })}</b>
      </div>
      <div role="listitem" className={s.kpi} title={`ETD ${fmt(d.etd)}`}>
        <IconShip size={16} aria-hidden="true" />
        <span>ETD</span>
        <b>{dayLabel(d.etd.slice(0, 10))}</b>
      </div>
      <div role="listitem" className={s.kpi} title="Transit">
        <IconRoute size={16} aria-hidden="true" />
        <span>Transit</span>
        <b>{t('jobDetail.days', { n: transitDays(d) })}</b>
      </div>
      <div role="listitem" className={s.kpi} title={t('jobDetail.gateInTip')}>
        <IconBox size={16} aria-hidden="true" />
        <span>{t('jobDetail.gateIn')}</span>
        <b>
          {gate.done}/{gate.total} cont
        </b>
      </div>
    </div>
  );
}

function CargoTab({ d }: { d: JobDetail }) {
  const t = useT();
  const toast = useToast();
  const phone = useLayout() === 'phone';
  return (
    <div className={s.cols}>
      <div className={s.col}>
        <Card title={t('jobDetail.cargoTotal')} icon={<IconBox size={18} aria-hidden="true" />}>
          <Kv items={d.cargo} />
        </Card>
        <Card title={t('jobDetail.contTitle')} icon={<IconBox size={18} aria-hidden="true" />}>
          {phone ? (
            <ul className={s.contList} aria-label="Container">
              {d.containers.map((c) => (
                <li key={c.no} className={s.contItem}>
                  <div className={s.contHead}>
                    <b className={s.mono}>{c.no}</b>
                    <Chip tone={c.gateIn ? 'ok' : 'warn'} label={t('jobDetail.gateIn')}>
                      {c.gateIn ? t('jobDetail.gateDone', { d: c.gateIn }) : t('jobDetail.gateNot')}
                    </Chip>
                  </div>
                  {!isValidContainerNo(c.no) && <div className={s.bad}>{t('jobDetail.badIso')}</div>}
                  <Kv
                    items={[
                      { label: t('jobDetail.colType'), value: c.type },
                      { label: 'Seal', value: c.seal },
                      { label: 'VGM', value: `${c.vgm.toLocaleString('vi-VN')} kg` },
                      { label: t('jobDetail.colPickup'), value: `${c.pickup} · ${c.freeNote}` },
                    ]}
                  />
                </li>
              ))}
            </ul>
          ) : (
          <div className={s.tableWrap}>
            <table className={s.table} aria-label="Container">
              <thead>
                <tr>
                  <th scope="col">{t('jobDetail.colNo')}</th>
                  <th scope="col">{t('jobDetail.colType')}</th>
                  <th scope="col">Seal</th>
                  <th scope="col" className={s.num}>
                    VGM
                  </th>
                  <th scope="col">{t('jobDetail.colPickup')}</th>
                  <th scope="col">{t('jobDetail.gateIn')}</th>
                </tr>
              </thead>
              <tbody>
                {d.containers.map((c) => (
                  <tr key={c.no}>
                    <td className={s.mono}>
                      {c.no}
                      {!isValidContainerNo(c.no) && <div className={s.bad}>{t('jobDetail.badIso')}</div>}
                    </td>
                    <td>{c.type}</td>
                    <td>{c.seal}</td>
                    <td className={s.num}>{c.vgm.toLocaleString('vi-VN')} kg</td>
                    <td>
                      {c.pickup}
                      <div className={s.sub}>{c.freeNote}</div>
                    </td>
                    <td>
                      <Chip tone={c.gateIn ? 'ok' : 'warn'} label={t('jobDetail.gateIn')}>
                        {c.gateIn ? t('jobDetail.gateDone', { d: c.gateIn }) : t('jobDetail.gateNot')}
                      </Chip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          <div className={s.cardFoot}>
            <Button size="sm" variant="secondary" onClick={() => toast({ msg: t('jobList.noWrite') })}>
              {t('jobDetail.addCont')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export function JobDetailPage() {
  const t = useT();
  const { jobId } = useParams({ strict: false }) as { jobId?: string };
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>('ov');
  const { data, error, isLoading, isError, refetch } = useRecord('jobDetail', jobId);
  const forced = readState();
  const layout = useLayout();
  const phone = layout === 'phone';

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (!data) state = 'empty';

  const d = data ?? null;
  const group = d ? jobGroup(d.status, null) : null;
  const noWrite = () => toast({ msg: t('jobList.noWrite') });
  const back = () => void navigate({ to: '/ezy/jobs' });
  const people = d ? (
    <span className={s.people} role="group" aria-label={t('jobDetail.peopleAria')}>
      {d.people.map((p) => (
        <span key={p.user} className={`${s.pic} ${HAS_PHOTO[p.user] ? s.picPhoto : ''}`} role="img" title={`${p.role} · ${p.user}`} aria-label={`${p.role} ${p.user}`}>
          {HAS_PHOTO[p.user] ? (
            <svg width="100%" height="100%" viewBox="0 0 40 40" aria-hidden="true">
              <circle cx="20" cy="16" r="7.5" className={s.picSkin} />
              <path d="M5 41c1.2-9.5 7.4-13.5 15-13.5S33.8 31.5 35 41z" className={s.picShirt} />
            </svg>
          ) : (
            p.user.slice(0, 2)
          )}
        </span>
      ))}
    </span>
  ) : null;
  const headInfo =
    d && group && state === 'ready' ? (
      <span className={s.sub0}>
        <button type="button" className={s.statusBtn} aria-label={phone ? `Đổi trạng thái, hiện ${d.status}` : t('jobDetail.changeStatus', { s: d.status })} onClick={noWrite}>
          <span className={s.statusDot} style={{ background: GROUP_DOT[group] }} aria-hidden="true" />
          {d.status}
          <span className={s.caret} aria-hidden="true">▾</span>
        </button>
        <span className={s.hChip}>{d.title}</span>
        {!phone && <span className={`${s.hChip} ${s.hChipPri}`}>Telex release</span>}
        {phone && people}
      </span>
    ) : undefined;

  return (
    <Page kind="detail">
      <PageHeader
        title={jobId ?? t('jobDetail.pageTitle')}
        noFind={!phone}
        lead={
          phone ? undefined : (
            <span className={s.backWrap}>
              <button type="button" className={s.back} aria-label={t('jobDetail.backAria')} title={t('jobDetail.listTitle')} onClick={back}>
                <IconChevronLeft size={16} stroke={2.2} aria-hidden="true" />
              </button>
            </span>
          )
        }
        {...{ [phone ? 'sub' : 'meta']: headInfo }}
        actions={
          phone ? (
            <>
              <button type="button" className={s.more} aria-label={t('jobDetail.backAria')} title={t('jobDetail.listTitle')} onClick={back}>
                <IconArrowLeft size={18} aria-hidden="true" />
              </button>
              <button type="button" className={s.more} aria-label={t('jobDetail.moreActions')} title={t('jobDetail.moreActions')} onClick={noWrite}>
                <IconDots size={18} aria-hidden="true" />
              </button>
            </>
          ) : (
            <>
              {people}
              <button type="button" className={s.hb} aria-label={t('jobDetail.searchAria')} title={t('jobDetail.searchTitle')} onClick={noWrite}>
                <IconSearch size={16} aria-hidden="true" />
              </button>
              <button type="button" className={s.hb} aria-label={t('jobDetail.propsAria')} title={t('jobDetail.propsAria')} onClick={noWrite}>
                <IconListDetails size={16} aria-hidden="true" />
              </button>
              <button type="button" className={s.hb} aria-label={t('jobDetail.tasksAria')} title={t('jobDetail.tasksAria')} onClick={noWrite}>
                <IconBell size={16} aria-hidden="true" />
                <span className={s.hbN}>{NOTI_N}</span>
              </button>
              <button type="button" className={s.hb} aria-label={t('jobDetail.moreActions')} title={t('jobDetail.moreActions')} onClick={noWrite}>
                <IconDots size={18} aria-hidden="true" />
              </button>
              <Button variant="primary" className={s.send} icon={<IconSend size={16} aria-hidden="true" />} onClick={noWrite}>
                {t('jobDetail.sendUpdate')}
              </Button>
            </>
          )
        }
      />
      <div className={s.page}>
        <StateView
          state={state === 'ready' && !d ? 'empty' : state}
          title={state === 'empty' || !d ? t('jobDetail.notFound', { id: jobId ?? '' }) : undefined}
          desc={state === 'empty' || !d ? t('jobDetail.notFoundDesc') : undefined}
          icon={state === 'empty' ? <IconCalendarEvent size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('jobDetail.retry')}</Button> : state === 'empty' ? <Button onClick={back}>{t('jobDetail.back')}</Button> : undefined}
        >
          {d && (
            <>
              {layout === 'ipad' && <Kpis d={d} />}
              {!phone && (
              <div className={s.tabRow}>
                <div role="tablist" aria-label={t('jobDetail.tabsAria')} className={s.tabs}>
                  {(layout === 'ipad' ? TABS.filter((tb) => tb.key !== 'svc') : TABS).map((tb) => (
                    <button key={tb.key} type="button" role="tab" aria-selected={tab === tb.key} className={`${s.tab} ${tab === tb.key ? s.tabOn : ''}`} onClick={() => setTab(tb.key)}>
                      {t(layout === 'ipad' && IPAD_LABEL[tb.key] ? IPAD_LABEL[tb.key]! : tb.label)}
                      {tb.n > 0 && <span className={s.tabN}>{tb.n}</span>}
                    </button>
                  ))}
                </div>
                {!phone && (
                  <span className={s.density} role="group" aria-label={t('jobDetail.density')}>
                    <button type="button" className={`${s.den} ${s.tabOn}`} aria-pressed="true" onClick={noWrite}>
                      {t('jobDetail.denStd')}
                    </button>
                    <button type="button" className={s.den} aria-pressed="false" onClick={noWrite}>
                      {t('jobDetail.denCompact')}
                    </button>
                  </span>
                )}
              </div>
              )}
              {tab === 'ov' && <OverviewBoard d={d} phone={phone} />}
              {tab === 'cargo' && <CargoTab d={d} />}
              {tab === 'note' && <Notes scope="job" id={d.id} notes={[]} onAdd={noWrite} />}
              {phone && (
                <>
                  <Button variant="primary" className={s.phFab} icon={<IconSend size={16} aria-hidden="true" />} onClick={noWrite}>
                    {t('jobDetail.sendUpdate')}
                  </Button>
                  <nav className={s.phNav} aria-label={t('jobDetail.tabsAria')}>
                    {PH_TABS.map((tb) => (
                      <button key={tb.key} type="button" className={`${s.phNavBtn} ${tab === tb.key ? s.phNavOn : ''}`} aria-current={tab === tb.key ? 'page' : undefined} onClick={() => setTab(tb.key)}>
                        {t(tb.label)}
                        {tb.n > 0 && <span className={s.tabN}>{tb.n}</span>}
                      </button>
                    ))}
                  </nav>
                </>
              )}
              {tab !== 'ov' && tab !== 'cargo' && tab !== 'note' && (
                <StateView state="empty" title={t('jobDetail.tabSoon')} desc={t('jobDetail.tabSoonDesc')} icon={<IconTimeline size={26} stroke={1.8} />} />
              )}
            </>
          )}
        </StateView>
      </div>
    </Page>
  );
}
